import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Spin,
  Flex,
  Form,
  Input,
  ConfigProvider,
  DatePicker,
  Select,
  Button,
} from "antd";
import { LoadingOutlined, CloseOutlined } from "@ant-design/icons";
import GoogleMapsInput from "../orders/GoogleMapsInput";
import locale from "antd/locale/ru_RU";
import "dayjs/locale/ru";
import { shippingTypes } from "./constants";
import { transportBodyTypes } from "./categories";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import FloatingField from "../common/FloatingField";
const { RangePicker } = DatePicker;

const OrderSearchForm = ({
  setOrders,
  setOrdersInitial,
  allOrders,
  setAllOrders,
  suppressQueryAutoSubmit = false,
  setSuppressQueryAutoSubmit,
}) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const watchedValues = Form.useWatch([], form) || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const [latLng, setLatLng] = useState({
    from: {},
    to: {},
  });

  const [city, setCity] = useState({
    from: null,
    to: null,
  });

  const [isInitialized, setIsInitialized] = useState(false);
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Add useEffect to filter when city state changes
  useEffect(() => {
    console.log("city changed", city);
    // Don't filter on initial render or before initialization
    if (isInitialized && (city.from !== null || city.to !== null)) {
      if (window.innerWidth > 992) filterOrders();
    }
  }, [city, isInitialized]);

  useEffect(() => {
    initializeFromQueryParams();
    fetchInitial();
  }, []);

  // Filter orders when both initialization and orders loading are complete
  useEffect(() => {
    if (isInitialized && ordersLoaded && allOrders) {
      const params = Object.fromEntries(searchParams.entries());
      if (Object.keys(params).length > 0 && !suppressQueryAutoSubmit) {
        // Apply filters from query params
        filterOrders();
      }
    }
  }, [isInitialized, ordersLoaded, allOrders, suppressQueryAutoSubmit]);

  // Initialize form from query parameters
  const initializeFromQueryParams = () => {
    const params = Object.fromEntries(searchParams.entries());

    if (Object.keys(params).length > 0) {
      const formValues = {};

      // Handle simple string/number fields
      const stringFields = ["from_city", "to_city"];
      const numberFields = [
        "type_of_transportation",
        "radiusFrom",
        "radiusTo",
        "weight_from",
        "weight_to",
        "volume_from",
        "volume_to",
        "price_from",
        "price_to",
      ];

      stringFields.forEach((field) => {
        if (params[field]) {
          formValues[field] = params[field];
        }
      });

      numberFields.forEach((field) => {
        if (params[field]) {
          const numValue = Number(params[field]);
          if (!isNaN(numValue)) {
            formValues[field] = numValue;
          }
        }
      });

      // Handle array fields
      if (params.transport_body_type) {
        try {
          formValues.transport_body_type = JSON.parse(
            params.transport_body_type,
          );
        } catch (e) {
          console.warn("Failed to parse transport_body_type from URL:", e);
        }
      }

      // Handle date range
      if (params.from_date && params.to_date) {
        formValues.dates = {
          from: params.from_date,
          to: params.to_date,
        };
      }

      // Handle location data
      if (params.lat_from && params.lng_from) {
        setLatLng((prev) => ({
          ...prev,
          from: {
            lat: parseFloat(params.lat_from),
            lng: parseFloat(params.lng_from),
          },
        }));
      }

      if (params.lat_to && params.lng_to) {
        setLatLng((prev) => ({
          ...prev,
          to: {
            lat: parseFloat(params.lat_to),
            lng: parseFloat(params.lng_to),
          },
        }));
      }

      if (params.city_from) {
        setCity((prev) => ({ ...prev, from: params.city_from }));
      }

      if (params.city_to) {
        setCity((prev) => ({ ...prev, to: params.city_to }));
      }

      // Set form values
      form.setFieldsValue(formValues);
    }

    setIsInitialized(true);
  };

  // Update query parameters when form changes
  const updateQueryParams = (values = form.getFieldsValue()) => {
    const params = new URLSearchParams();

    // Add simple fields
    [
      "type_of_transportation",
      "radiusFrom",
      "radiusTo",
      "weight_from",
      "weight_to",
      "volume_from",
      "volume_to",
      "price_from",
      "price_to",
      "from_city",
      "to_city",
    ].forEach((field) => {
      if (values[field]) {
        params.set(field, values[field]);
      }
    });

    // Add array fields
    if (values.transport_body_type && values.transport_body_type.length > 0) {
      params.set(
        "transport_body_type",
        JSON.stringify(values.transport_body_type),
      );
    }

    // Add date range
    if (values.dates) {
      if (values.dates.from) params.set("from_date", values.dates.from);
      if (values.dates.to) params.set("to_date", values.dates.to);
    }

    // Add location data
    if (latLng.from.lat && latLng.from.lng) {
      params.set("lat_from", latLng.from.lat.toString());
      params.set("lng_from", latLng.from.lng.toString());
    }

    if (latLng.to.lat && latLng.to.lng) {
      params.set("lat_to", latLng.to.lat.toString());
      params.set("lng_to", latLng.to.lng.toString());
    }

    if (city.from) params.set("city_from", city.from);
    if (city.to) params.set("city_to", city.to);

    setSearchParams(params, { replace: true });
  };

  const fetchInitial = async () => {
    setLoading(true);
    try {
      const { status, data } = await axios({
        url: `${process.env.REACT_APP_API_URL}/order_search/`,
        method: "POST",
        data: {},
      });
      console.log(data);
      // Filter orders where to_date is not less than current UTC date
      // Current date converted to UTC (start of day)
      const now = new Date();
      const currentUTCDateStart = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
      );

      const filteredData = data.filter((order) => {
        if (order.status != 1) return false;

        // Check if to_date exists and is not less than current UTC date
        if (order.to_date) {
          const orderToDate = new Date(order.to_date);
          // Convert order.to_date to end of day UTC for comparison
          const orderToDateEndOfDayUTC = new Date(
            Date.UTC(
              orderToDate.getUTCFullYear(),
              orderToDate.getUTCMonth(),
              orderToDate.getUTCDate(),
              23,
              59,
              59,
              999,
            ),
          );

          return orderToDateEndOfDayUTC >= currentUTCDateStart;
        }

        return true;
      });
      setOrders(filteredData);
      setAllOrders(filteredData);
      setOrdersLoaded(true);
    } catch (error) {
      console.log(error);
      setOrdersLoaded(true); // Set to true even on error to prevent hanging
    } finally {
      setLoading(false);
    }
  };

  // Filter orders based on current form values
  const filterOrders = (values = form.getFieldsValue()) => {
    console.log("filterEntity");
    if (!allOrders || !isInitialized) return;

    if (setSuppressQueryAutoSubmit) {
      setSuppressQueryAutoSubmit(false);
    }
    setLoading(true);

    // Update query parameters
    updateQueryParams(values);

    let filtered = [...allOrders];

    if (values.type_of_transportation) {
      filtered = filtered.filter(
        (item) =>
          +item.type_of_transportation === +values.type_of_transportation,
      );
    }

    if (
      values.transport_body_type &&
      values.transport_body_type.length > 0
    ) {
      const selectedBodyTypes = values.transport_body_type.map(Number);

      filtered = filtered.filter((item) =>
        selectedBodyTypes.includes(Number(item.transport_body_type)),
      );
    }

    if (values.radiusFrom && latLng.from.lat && latLng.from.lng) {
      filtered = filtered.filter((item) => {
        if (!item.location_from || !item.location_to) return false;

        const distance = calculateDistance(
          latLng.from.lat,
          latLng.from.lng,
          item.location_from.lat,
          item.location_from.lng,
        );
        return distance <= values.radiusFrom * 1000;
      });
    } else if (city.from) {
      filtered = filtered.filter(
        (item) =>
          item.from_address.includes(city.from) ||
          item.from_address === city.from,
      );
    }

    if (values.radiusTo && latLng.to.lat && latLng.to.lng) {
      filtered = filtered.filter((item) => {
        if (!item.location_to || !item.location_to) return false;

        const distance = calculateDistance(
          latLng.to.lat,
          latLng.to.lng,
          item.location_to.lat,
          item.location_to.lng,
        );

        return distance <= values.radiusTo * 1000;
      });
    } else if (city.to) {
      filtered = filtered.filter(
        (item) =>
          item.to_address.includes(city.to) || item.to_address === city.to,
      );
    }

    // Filter by date range if provided
    if (values.dates && values.dates.from && values.dates.to) {
      const selectedFromDate = new Date(values.dates.from).getTime();
      const selectedToDate = new Date(values.dates.to).getTime();

      console.log(
        "Selected date range:",
        values.dates.from,
        "to",
        values.dates.to,
      );
      console.log(
        "Selected timestamps:",
        selectedFromDate,
        "to",
        selectedToDate,
      );

      filtered = filtered.filter((item) => {
        if (!item.from_date || !item.to_date) return false;

        const itemFromDate = new Date(item.from_date).getTime();
        const itemToDate = new Date(item.to_date).getTime();

        // Strict containment: order must be completely within selected range
        const result =
          itemFromDate >= selectedFromDate && itemToDate <= selectedToDate;

        console.log(
          `Order ${item.uuid}: ${item.from_date} to ${item.to_date} (${itemFromDate} to ${itemToDate}) - ${result ? "INCLUDED" : "EXCLUDED"}`,
        );

        return result;
      });
      console.log("Filtered count:", filtered.length);
    }

    if (values.weight_from) {
      filtered = filtered.filter(
        (item) => item.item.weight >= values.weight_from,
      );
    }
    if (values.weight_to) {
      filtered = filtered.filter(
        (item) => item.item.weight <= values.weight_to,
      );
    }
    if (values.volume_from) {
      filtered = filtered.filter(
        (item) => item.item.volume >= values.volume_from,
      );
    }
    if (values.volume_to) {
      filtered = filtered.filter(
        (item) => item.item.volume <= values.volume_to,
      );
    }
    if (values.price_from) {
      filtered = filtered.filter((item) => item.price >= values.price_from);
    }
    if (values.price_to) {
      filtered = filtered.filter((item) => item.price <= values.price_to);
    }

    // Apply status filter and date filter for to_date not less than current UTC date
    // Current date converted to UTC (start of day)
    const now = new Date();
    const currentUTCDateStart = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
    );

    setOrders(
      filtered.filter((order) => {
        if (order.status != 1) return false;

        // Check if to_date exists and is not less than current UTC date
        if (order.to_date) {
          const orderToDate = new Date(order.to_date);
          // Convert order.to_date to end of day UTC for comparison
          const orderToDateEndOfDayUTC = new Date(
            Date.UTC(
              orderToDate.getUTCFullYear(),
              orderToDate.getUTCMonth(),
              orderToDate.getUTCDate(),
              23,
              59,
              59,
              999,
            ),
          );

          return orderToDateEndOfDayUTC >= currentUTCDateStart;
        }

        return true;
      }),
    );
    setOrdersInitial(false);
    setLoading(false);
  };

  const clearForm = () => {
    form.resetFields();
    setCity({ from: null, to: null });
    setLatLng({ from: {}, to: {} });
    setSearchParams({}, { replace: true });

    // Apply status filter and date filter for to_date not less than current UTC date
    // Current date converted to UTC (start of day)
    const now = new Date();
    const currentUTCDateStart = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
    );

    setOrders(
      allOrders?.filter((order) => {
        if (order.status != 1) return false;

        // Check if to_date exists and is not less than current UTC date
        if (order.to_date) {
          const orderToDate = new Date(order.to_date);
          // Convert order.to_date to end of day UTC for comparison
          const orderToDateEndOfDayUTC = new Date(
            Date.UTC(
              orderToDate.getUTCFullYear(),
              orderToDate.getUTCMonth(),
              orderToDate.getUTCDate(),
              23,
              59,
              59,
              999,
            ),
          );

          return orderToDateEndOfDayUTC >= currentUTCDateStart;
        }

        return true;
      }) || [],
    );
    setOrdersInitial(true);
  };

  useEffect(() => {
    window.addEventListener('clearOrders', clearForm);
    return () => window.removeEventListener('clearOrders', clearForm);
  });

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  // Form field value change handler to trigger filtering
  const onFieldsChange = (changedFields, allFields) => {
    if (isInitialized) {
      if (window.innerWidth > 992) filterOrders();
    }
  };

  const reverseLocations = () => {
    const { from, to } = city;
    setCity({
      from: to,
      to: from,
    });
    setLatLng({
      from: latLng.to,
      to: latLng.from,
    });
    form.setFieldsValue({
      radiusFrom: form.getFieldValue("radiusTo"),
      radiusTo: form.getFieldValue("radiusFrom"),
    });
    form.setFieldsValue({
      from_city: form.getFieldValue("to_city"),
      to_city: form.getFieldValue("from_city"),
    });
  };

  const datePickerChange = (val) => {
    if (val && val.length === 2) {
      form.setFieldsValue({
        dates: {
          from: val[0].format("YYYY-MM-DD"),
          to: val[1].format("YYYY-MM-DD"),
        },
      });
      if (window.innerWidth > 992) filterOrders();
    }
  };

  return (
    <div className="transport-search-form">
      {loading && !isMobile && (
        <div className="transport-search-full-screen">
          <Flex align="center" gap="middle">
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            />
          </Flex>
        </div>
      )}
      <Form
        form={form}
        name="basic"
        onFinish={filterOrders}
        onFinishFailed={onFinishFailed}
        onFieldsChange={onFieldsChange}
        autoComplete="off"
        layout="vertical"
      >
        <div className="transport-search-form__row transport-search-form__row--places">
          <div className="transport-search-form__places">
            <GoogleMapsInput
              form={form}
              setLatLng={setLatLng}
              setCity={setCity}
              direction="from"
              field="from_city"
              latLng={latLng}
              city={city}
              placeholder="Откуда"
              required={false}
              floatingLabel
            />
            <div className="transport-search-form__places__radius">
              <Form.Item
                name="radiusFrom"
                label=" "
                disabled={latLng.from.lat === 0 || latLng.from.lat === null}
              >
                <FloatingField label="Радиус" active={watchedValues.radiusFrom}>
                  <Input
                    placeholder=""
                    size="large"
                    disabled={!latLng.from.lat}
                  />
                </FloatingField>
              </Form.Item>
            </div>
          </div>
          <div
            className="transport-search-form__reverse"
            onClick={reverseLocations}
          />
          <div className="transport-search-form__places">
            <GoogleMapsInput
              form={form}
              setLatLng={setLatLng}
              setCity={setCity}
              direction="to"
              field="to_city"
              latLng={latLng}
              city={city}
              placeholder="Куда"
              required={false}
              floatingLabel
            />
            <div className="transport-search-form__places__radius">
              <Form.Item name="radiusTo" label=" ">
                <FloatingField label="Радиус" active={watchedValues.radiusTo}>
                  <Input
                    placeholder=""
                    size="large"
                    disabled={!latLng.to.lat}
                  />
                </FloatingField>
              </Form.Item>
            </div>
          </div>
        </div>
        <div className="transport-search-form__row transport-search-form__row--stack-mobile">
          <div className="transport-search-form__row transport-search-form__row--child-full">
            <Form.Item name="dates" label=" ">
              <ConfigProvider locale={locale}>
                <FloatingField label="Дата погрузки" active={watchedValues.dates}>
                  <RangePicker
                    placeholder={["", ""]}
                    onChange={datePickerChange}
                    locale={locale}
                    size="large"
                    inputReadOnly={isMobile}
                    placement={isMobile ? "bottomLeft" : undefined}
                    popupClassName={
                      isMobile ? "mobile-range-picker-dropdown" : undefined
                    }
                  />
                </FloatingField>
              </ConfigProvider>
            </Form.Item>
          </div>
          <div className="transport-search-form__divider desktop-only" />
          <div className="transport-search-form__row transport-search-form__row--child-full">
            <Form.Item name="type_of_transportation" label=" ">
              <FloatingField
                label="Тип перевозки"
                active={watchedValues.type_of_transportation !== undefined}
              >
                <Select
                  placeholder=""
                  options={[{ value: "", label: "Все" }, ...shippingTypes]}
                  size="large"
                />
              </FloatingField>
            </Form.Item>
          </div>
        </div>
        <div className="transport-search-form__row transport-search-form__row--child-full mobile-only">
          <Form.Item name="transport_body_type" label=" ">
            <FloatingField label="Тип кузова" active={watchedValues.transport_body_type}>
              <Select placeholder="" size="large" mode="multiple">
                {useMemo(() => {
                  // Find all parent body types (with parent_id: null)
                  const parentBodyTypes = transportBodyTypes.filter(
                    (item) => item.parent_id === null,
                  );

                  return parentBodyTypes.map((parent) => (
                    <Select.OptGroup key={parent.id} label={parent.name}>
                      {transportBodyTypes
                        .filter((child) => child.parent_id === parent.id)
                        .map((child) => (
                          <Select.Option key={child.id} value={child.id}>
                            {child.name}
                          </Select.Option>
                        ))}
                    </Select.OptGroup>
                  ));
                }, [])}
              </Select>
            </FloatingField>
          </Form.Item>
        </div>
        <div className="transport-search-form__row transport-search-form__row--stack-mobile">
          <div className="transport-search-form__row two-way">
            <Form.Item name="weight_from" label=" ">
              <FloatingField label="Вес от" active={watchedValues.weight_from}>
                <Input className="suffix-input" prefix="" type="number" size="large" />
              </FloatingField>
            </Form.Item>
            <Form.Item name="weight_to" label=" ">
              <FloatingField label="до (тонны)" active={watchedValues.weight_to}>
                <Input className="suffix-input" prefix="" type="number" size="large" />
              </FloatingField>
            </Form.Item>
          </div>
          <div className="transport-search-form__row two-way">
            <Form.Item name="volume_from" label=" ">
              <FloatingField label="Объём от" active={watchedValues.volume_from}>
                <Input className="suffix-input" type="number" prefix="" size="large" />
              </FloatingField>
            </Form.Item>
            <Form.Item name="volume_to" label=" ">
              <FloatingField label="до (м3)" active={watchedValues.volume_to}>
                <Input className="suffix-input" type="number" prefix="" size="large" />
              </FloatingField>
            </Form.Item>
          </div>
        </div>
        <div className="transport-search-form__row transport-search-form__row--stack-mobile">
          <div className="transport-search-form__row transport-search-form__row--child-full desktop-only">
            <Form.Item name="transport_body_type" label=" ">
              <FloatingField label="Тип кузова" active={watchedValues.transport_body_type}>
                <Select placeholder="" size="large" mode="multiple">
                  {useMemo(() => {
                    // Find all parent body types (with parent_id: null)
                    const parentBodyTypes = transportBodyTypes.filter(
                      (item) => item.parent_id === null,
                    );

                    return parentBodyTypes.map((parent) => (
                      <Select.OptGroup key={parent.id} label={parent.name}>
                        {transportBodyTypes
                          .filter((child) => child.parent_id === parent.id)
                          .map((child) => (
                            <Select.Option key={child.id} value={child.id}>
                              {child.name}
                            </Select.Option>
                          ))}
                      </Select.OptGroup>
                    ));
                  }, [])}
                </Select>
              </FloatingField>
            </Form.Item>
          </div>
          <div className="transport-search-form__row two-way">
            <Form.Item name="price_from" label=" ">
              <FloatingField label="Цена от" active={watchedValues.price_from}>
                <Input className="suffix-input" prefix="" type="number" size="large" />
              </FloatingField>
            </Form.Item>
            <Form.Item name="price_to" label=" ">
              <FloatingField label="до" active={watchedValues.price_to}>
                <Input className="suffix-input" prefix="" type="number" size="large" />
              </FloatingField>
            </Form.Item>
          </div>
        </div>
        <Flex justify="space-between" className="transport-search-bottom-buttons">
          <Button
            type="primary"
            className="mobile-only transport-search-submit"
            onClick={() => form.submit()}
          >
            Применить
          </Button>
          <div className="transport-search-form__clear" onClick={clearForm}>
            <CloseOutlined style={{ fontSize: 16 }} />
            Сбросить
          </div>
        </Flex>
      </Form>
    </div>
  );
};

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return distance; // Distance in meters
};

// Convert degrees to radians
const toRad = (value) => {
  return (value * Math.PI) / 180;
};

export default OrderSearchForm;
